import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { 
  LanguageCode, 
  LanguageOption, 
  SUPPORTED_LANGUAGES, 
  TRANSLATIONS 
} from './translations';

export type { LanguageCode, LanguageOption };
export { SUPPORTED_LANGUAGES, TRANSLATIONS };

interface I18nContextType {
  lang: LanguageCode;
  changeLanguage: (code: LanguageCode) => void;
  t: (key: string, defaultText?: string) => string;
  supportedLanguages: LanguageOption[];
  isHindi: boolean;
  currentLanguageOption: LanguageOption;
}

const I18nContext = createContext<I18nContextType | null>(null);

const STORAGE_KEY = 'azuraos_language';

const VALID_CODES = new Set<string>(SUPPORTED_LANGUAGES.map(l => l.code));

export function getInitialLanguage(): LanguageCode {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const saved = window.localStorage.getItem(STORAGE_KEY) as LanguageCode;
      if (saved && VALID_CODES.has(saved)) {
        return saved;
      }
    }
  } catch (e) {
    console.warn('Could not read language from localStorage:', e);
  }
  return 'en';
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLang] = useState<LanguageCode>(getInitialLanguage);

  useEffect(() => {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem(STORAGE_KEY, lang);
      }
      // Set HTML document language for screen readers and SEO
      document.documentElement.lang = lang;
    } catch (e) {
      console.warn('Could not persist language to localStorage:', e);
    }

    // Broadcast across multiple browser tabs/windows
    const handleStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && e.newValue && VALID_CODES.has(e.newValue)) {
        setLang(e.newValue as LanguageCode);
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [lang]);

  const changeLanguage = (newLang: LanguageCode) => {
    if (VALID_CODES.has(newLang)) {
      setLang(newLang);
      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          window.localStorage.setItem(STORAGE_KEY, newLang);
        }
      } catch (e) {
        console.warn('Could not save language to localStorage:', e);
      }
    }
  };

  const t = (key: string, defaultText?: string): string => {
    const langDict = TRANSLATIONS[lang] || TRANSLATIONS.en;
    if (langDict && langDict[key] !== undefined && langDict[key] !== '') {
      return langDict[key];
    }
    const enDict = TRANSLATIONS.en;
    if (enDict && enDict[key] !== undefined && enDict[key] !== '') {
      return enDict[key];
    }
    return defaultText !== undefined ? defaultText : key;
  };

  const currentLanguageOption = SUPPORTED_LANGUAGES.find(l => l.code === lang) || SUPPORTED_LANGUAGES[0];

  const value: I18nContextType = {
    lang,
    changeLanguage,
    t,
    supportedLanguages: SUPPORTED_LANGUAGES,
    isHindi: lang === 'hi',
    currentLanguageOption,
  };

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextType {
  const context = useContext(I18nContext);
  if (!context) {
    const fallbackLang = getInitialLanguage();
    const currentLanguageOption = SUPPORTED_LANGUAGES.find(l => l.code === fallbackLang) || SUPPORTED_LANGUAGES[0];
    return {
      lang: fallbackLang,
      changeLanguage: () => {},
      t: (key: string, defaultText?: string) => {
        const dict = TRANSLATIONS[fallbackLang] || TRANSLATIONS.en;
        return dict[key] || TRANSLATIONS.en[key] || defaultText || key;
      },
      supportedLanguages: SUPPORTED_LANGUAGES,
      isHindi: fallbackLang === 'hi',
      currentLanguageOption,
    };
  }
  return context;
}

export function getCurrentLanguage(): LanguageCode {
  return getInitialLanguage();
}

export function setLanguage(lang: LanguageCode): void {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(STORAGE_KEY, lang);
    }
  } catch (e) {
    console.warn('Could not set language:', e);
  }
}

export function translate(key: string, lang?: LanguageCode): string {
  const activeLang = lang || getInitialLanguage();
  const dict = TRANSLATIONS[activeLang] || TRANSLATIONS.en;
  return dict[key] || TRANSLATIONS.en[key] || key;
}

export interface TranslationEntry {
  key: string;
  namespace: string;
  en: string;
  hi: string;
  targetLang?: LanguageCode;
  targetValue?: string;
  allValues?: Partial<Record<LanguageCode, string>>;
  isComplete: boolean;
  status: 'synced' | 'missing_hi' | 'missing_en' | 'missing_target' | 'empty';
}

export interface LanguageCoverageStat {
  code: LanguageCode;
  label: string;
  nativeLabel: string;
  flag: string;
  region: string;
  count: number;
  coveragePercent: number;
  isComplete: boolean;
}

export interface TranslationStats {
  totalUniqueKeys: number;
  enCount: number;
  hiCount: number;
  missingInHindi: string[];
  missingInEnglish: string[];
  enCoveragePercent: number;
  hiCoveragePercent: number;
  is100PercentComplete: boolean;
  namespaces: string[];
  languageCoverages: LanguageCoverageStat[];
}

export function getTranslationStats(): TranslationStats {
  // Collect all unique keys across all 9 supported languages
  const keySet = new Set<string>();
  SUPPORTED_LANGUAGES.forEach(langOpt => {
    const dict = TRANSLATIONS[langOpt.code] || {};
    Object.keys(dict).forEach(k => keySet.add(k));
  });

  const allKeys = Array.from(keySet).sort();
  const totalUniqueKeys = allKeys.length;

  const enKeys = Object.keys(TRANSLATIONS.en || {});
  const hiKeys = Object.keys(TRANSLATIONS.hi || {});

  const missingInHindi = allKeys.filter(k => !TRANSLATIONS.hi || TRANSLATIONS.hi[k] === undefined || TRANSLATIONS.hi[k].trim() === '');
  const missingInEnglish = allKeys.filter(k => !TRANSLATIONS.en || TRANSLATIONS.en[k] === undefined || TRANSLATIONS.en[k].trim() === '');

  const enCount = enKeys.filter(k => (TRANSLATIONS.en[k] || '').trim().length > 0).length;
  const hiCount = hiKeys.filter(k => (TRANSLATIONS.hi[k] || '').trim().length > 0).length;

  const enCoveragePercent = totalUniqueKeys > 0 ? Math.round((enCount / totalUniqueKeys) * 100) : 100;
  const hiCoveragePercent = totalUniqueKeys > 0 ? Math.round((hiCount / totalUniqueKeys) * 100) : 100;

  const namespaces = Array.from(
    new Set(allKeys.map(k => (k.includes('.') ? k.split('.')[0] : 'general')))
  ).sort();

  const languageCoverages: LanguageCoverageStat[] = SUPPORTED_LANGUAGES.map(opt => {
    const dict = TRANSLATIONS[opt.code] || {};
    const count = allKeys.filter(k => (dict[k] || '').trim().length > 0).length;
    const coveragePercent = totalUniqueKeys > 0 ? Math.round((count / totalUniqueKeys) * 100) : 100;
    return {
      code: opt.code,
      label: opt.label,
      nativeLabel: opt.nativeLabel,
      flag: opt.flag,
      region: opt.region,
      count,
      coveragePercent,
      isComplete: count >= totalUniqueKeys
    };
  });

  return {
    totalUniqueKeys,
    enCount,
    hiCount,
    missingInHindi,
    missingInEnglish,
    enCoveragePercent,
    hiCoveragePercent,
    is100PercentComplete: missingInHindi.length === 0 && missingInEnglish.length === 0,
    namespaces,
    languageCoverages,
  };
}

export function getAllTranslationEntries(targetLang: LanguageCode = 'hi'): TranslationEntry[] {
  const keySet = new Set<string>();
  SUPPORTED_LANGUAGES.forEach(langOpt => {
    const dict = TRANSLATIONS[langOpt.code] || {};
    Object.keys(dict).forEach(k => keySet.add(k));
  });

  const allKeys = Array.from(keySet).sort();

  return allKeys.map(key => {
    const enVal = TRANSLATIONS.en?.[key] ?? '';
    const hiVal = TRANSLATIONS.hi?.[key] ?? '';
    const targetVal = TRANSLATIONS[targetLang]?.[key] ?? '';
    const namespace = key.includes('.') ? key.split('.')[0] : 'general';

    const allValues: Partial<Record<LanguageCode, string>> = {};
    SUPPORTED_LANGUAGES.forEach(opt => {
      allValues[opt.code] = TRANSLATIONS[opt.code]?.[key] ?? '';
    });

    let status: 'synced' | 'missing_hi' | 'missing_en' | 'missing_target' | 'empty' = 'synced';
    if (!enVal && !targetVal) {
      status = 'empty';
    } else if (!targetVal) {
      status = 'missing_target';
    } else if (!enVal) {
      status = 'missing_en';
    } else if (!hiVal) {
      status = 'missing_hi';
    }

    return {
      key,
      namespace,
      en: enVal,
      hi: hiVal,
      targetLang,
      targetValue: targetVal,
      allValues,
      isComplete: Boolean(enVal && targetVal),
      status,
    };
  });
}
